import { EXPLORED_HEADER_BYTES, HEADER_BYTES, MessageType, applySnapshot, decodeCombat, decodeEdgeView, decodeEffects, decodeExplored, decodeHarvestEvent, decodeHarvestList, decodeLooks, decodePartyPositions, decodePartyVitals, decodeProjectiles, decodePvpState, decodeProgress, decodePvpSwitch, decodeSnapshot, decodeTargetEffects, decodeThingEvent, decodeVitals, exploredFrameBytes, frameType, newCombatRecord, newEdgeViewRecord, newExploredSet, newEffectsRecord, newHarvestEventRecord, newHarvestListRecord, newLooksRecord, newPartyPositionsRecord, newPartyVitalsRecord, newProjectilesRecord, newPvpStateRecord, newProgressRecord, newPvpSwitchRecord, newSnapshotRecord, newTargetEffectsRecord, newThingEventRecord, newVitalsRecord, type ClientView, type CombatRecord, type EdgeViewRecord, type EffectsRecord, type ExploredSet, type HarvestEventRecord, type HarvestListRecord, type LooksRecord, type PartyPositionsRecord, type PartyVitalsRecord, type ProgressRecord, type ProjectilesRecord, type PvpStateRecord, type PvpSwitchRecord, type SnapshotRecord, type TargetEffectsRecord, type ThingEventRecord, type VitalsRecord } from "odyssey-shared";

/**
 * The server's binary frames as the page reads them (ADR-074 decision 11): the type at offset 1 chooses the decoder,
 * so a snapshot is applied to the view, `vitals`, `pvpState`, `pvpSwitch` and `progress` (ADR-083 decision 6) are handed
 * to the store the HUD draws, the
 * owner's `effects` frame is handed to the fight and the predictor (ADR-079 decision 9), the target's `targetEffects`
 * to the bar's status row (ADR-079's amendment for P2.2-T4), a `combat` frame's events are
 * decoded and handed on (P2.1-T4 draws them), a `thingEvent` frame's events on things in view are handed on as they
 * are decoded, unknown codes among them (ADR-096 decision 22), a `looks` frame's characters are handed on, each look as
 * it travelled (ADR-094 decision 6), a `projectiles` frame's launches are handed on as they were sent, every value of
 * every field among them, since the frame's only faults are its length, version and type (ADR-095 decision 12), and a
 * server message of a type this page does not handle is ignored with one warning per type. Only a snapshot that fails to decode or apply, or
 * a frame too short to name a type, is counted as a discarded snapshot; no other server message ever is.
 */

/**
 * What the dispatch hands on. `onCombat` hears each `combat` frame, `onPvpState` each `pvpState`, `onPvpSwitch` each
 * `pvpSwitch` (ADR-076), `onProgress` each `progress` (ADR-083), `onEffects` each `effects` frame (ADR-079) and
 * `onTargetEffects` each `targetEffects` frame (ADR-079's amendment for P2.2-T4) and `onThingEvent` each `thingEvent`
 * frame (ADR-096) and `onLooks` each `looks` frame, which arrives before the snapshot of its tick (ADR-094 decision
 * 6), and `onProjectiles` each `projectiles` frame, which follows the snapshot of its tick (ADR-095 decision 15), each
 * record reused for the next, and `onExplored` the `explored` frame of a join as a set of its own counts (ADR-100
 * decision 22), a new set each time, since it is sent once a join. `warn` writes a
 * warning, the console's unless a test hands in another.
 */
export interface BinaryHandlers {
  readonly onSnapshot: (record: SnapshotRecord, view: ClientView) => void;
  readonly onVitals: (vitals: Readonly<VitalsRecord>) => void;
  readonly onEffects?: (effects: Readonly<EffectsRecord>) => void;
  readonly onTargetEffects?: (effects: Readonly<TargetEffectsRecord>) => void;
  readonly onPvpState?: (pvp: Readonly<PvpStateRecord>) => void;
  readonly onPvpSwitch?: (pvpSwitch: Readonly<PvpSwitchRecord>) => void;
  readonly onProgress?: (progress: Readonly<ProgressRecord>) => void;
  readonly onCombat?: (combat: Readonly<CombatRecord>) => void;
  readonly onThingEvent?: (events: Readonly<ThingEventRecord>) => void;
  readonly onHarvested?: (list: Readonly<HarvestListRecord>) => void;
  readonly onHarvestEvent?: (events: Readonly<HarvestEventRecord>) => void;
  readonly onLooks?: (looks: Readonly<LooksRecord>) => void;
  readonly onProjectiles?: (projectiles: Readonly<ProjectilesRecord>) => void;
  readonly onExplored?: (set: ExploredSet) => void;
  readonly onPartyVitals?: (vitals: Readonly<PartyVitalsRecord>) => void;
  readonly onPartyPositions?: (positions: Readonly<PartyPositionsRecord>) => void;
  /**
   * Hears each `edgeView` (PROTOCOL.md 5.24, ADR-147 decision 13): a neighbour's band of entities in its own frame and
   * handles, with its map's index in the world manifest. The record and its entities are valid only during the call.
   */
  readonly onEdgeView?: (view: Readonly<EdgeViewRecord>) => void;
  readonly onDiscarded: () => void;
  readonly warn?: (message: string) => void;
}

/**
 * How many characters the `looks` record is made for: a view's worth, so no frame allocates in the decoder (ADR-094
 * note 14).
 */
const VIEW_LOOKS = 512;

/**
 * One connection's binary dispatch, with the records its decoders fill, reused for every frame.
 */
export class BinaryDispatch {
  private readonly record = newSnapshotRecord();
  private readonly vitals = newVitalsRecord();
  private readonly combat = newCombatRecord();
  private readonly pvp = newPvpStateRecord();
  private readonly pvpSwitch = newPvpSwitchRecord();
  private readonly progress = newProgressRecord();
  private readonly effects = newEffectsRecord();
  private readonly targetEffects = newTargetEffectsRecord();
  private readonly thingEvents = newThingEventRecord();
  private readonly harvestList = newHarvestListRecord();
  private readonly harvestEvents = newHarvestEventRecord();
  private readonly looks = newLooksRecord(VIEW_LOOKS);
  private readonly projectiles = newProjectilesRecord();
  private readonly partyVitals = newPartyVitalsRecord();
  private readonly partyPositions = newPartyPositionsRecord();
  private readonly edgeView = newEdgeViewRecord();
  private readonly warned = new Set<string>();

  /**
   * @param view The client view snapshots apply to.
   * @param handlers Where decoded frames go.
   */
  public constructor(
    private readonly view: ClientView,
    private readonly handlers: BinaryHandlers,
  ) {}

  /**
   * Reads one binary frame by its type.
   *
   * @param frame The frame bytes.
   */
  public receive(frame: Uint8Array): void {
    const type = frameType(frame);
    if (type === MessageType.Snapshot || type < 0) {
      if (decodeSnapshot(frame, this.record).kind !== "ok" || !applySnapshot(this.view, this.record)) {
        this.handlers.onDiscarded();
        return;
      }
      this.handlers.onSnapshot(this.record, this.view);
      return;
    }
    if (type === MessageType.Vitals) {
      if (decodeVitals(frame, this.vitals).kind !== "ok") {
        this.warnOnce("vitals", "ignoring a vitals frame that does not decode");
        return;
      }
      this.handlers.onVitals(this.vitals);
      return;
    }
    if (type === MessageType.Effects) {
      if (decodeEffects(frame, this.effects).kind !== "ok") {
        this.warnOnce("effects", "ignoring an effects frame that does not decode");
        return;
      }
      this.handlers.onEffects?.(this.effects);
      return;
    }
    if (type === MessageType.TargetEffects) {
      if (decodeTargetEffects(frame, this.targetEffects).kind !== "ok") {
        this.warnOnce("targetEffects", "ignoring a targetEffects frame that does not decode");
        return;
      }
      this.handlers.onTargetEffects?.(this.targetEffects);
      return;
    }
    if (type === MessageType.PvpState) {
      if (decodePvpState(frame, this.pvp).kind !== "ok") {
        this.warnOnce("pvpState", "ignoring a pvpState frame that does not decode");
        return;
      }
      this.handlers.onPvpState?.(this.pvp);
      return;
    }
    if (type === MessageType.PvpSwitch) {
      if (decodePvpSwitch(frame, this.pvpSwitch).kind !== "ok") {
        this.warnOnce("pvpSwitch", "ignoring a pvpSwitch frame that does not decode");
        return;
      }
      this.handlers.onPvpSwitch?.(this.pvpSwitch);
      return;
    }
    if (type === MessageType.Progress) {
      if (decodeProgress(frame, this.progress).kind !== "ok") {
        this.warnOnce("progress", "ignoring a progress frame that does not decode");
        return;
      }
      this.handlers.onProgress?.(this.progress);
      return;
    }
    if (type === MessageType.Combat) {
      if (decodeCombat(frame, this.combat).kind !== "ok") {
        this.warnOnce("combat", "ignoring a combat frame that does not decode");
        return;
      }
      this.handlers.onCombat?.(this.combat);
      return;
    }
    if (type === MessageType.ThingEvent) {
      if (decodeThingEvent(frame, this.thingEvents).kind !== "ok") {
        this.warnOnce("thingEvent", "ignoring a thingEvent frame that does not decode");
        return;
      }
      this.handlers.onThingEvent?.(this.thingEvents);
      return;
    }
    if (type === MessageType.Harvested) {
      if (decodeHarvestList(frame, this.harvestList).kind !== "ok") {
        this.warnOnce("harvested", "ignoring a harvested frame that does not decode");
        return;
      }
      this.handlers.onHarvested?.(this.harvestList);
      return;
    }
    if (type === MessageType.HarvestEvent) {
      if (decodeHarvestEvent(frame, this.harvestEvents).kind !== "ok") {
        this.warnOnce("harvestEvent", "ignoring a harvestEvent frame that does not decode");
        return;
      }
      this.handlers.onHarvestEvent?.(this.harvestEvents);
      return;
    }
    if (type === MessageType.Looks) {
      if (decodeLooks(frame, this.looks).kind !== "ok") {
        this.warnOnce("looks", "ignoring a looks frame that does not decode");
        return;
      }
      this.handlers.onLooks?.(this.looks);
      return;
    }
    if (type === MessageType.Projectiles) {
      if (decodeProjectiles(frame, this.projectiles).kind !== "ok") {
        this.warnOnce("projectiles", "ignoring a projectiles frame that does not decode");
        return;
      }
      this.handlers.onProjectiles?.(this.projectiles);
      return;
    }
    if (type === MessageType.PartyVitals) {
      if (decodePartyVitals(frame, this.partyVitals).kind !== "ok") {
        this.warnOnce("partyVitals", "ignoring a partyVitals frame that does not decode");
        return;
      }
      this.handlers.onPartyVitals?.(this.partyVitals);
      return;
    }
    if (type === MessageType.PartyPositions) {
      if (decodePartyPositions(frame, this.partyPositions).kind !== "ok") {
        this.warnOnce("partyPositions", "ignoring a partyPositions frame that does not decode");
        return;
      }
      this.handlers.onPartyPositions?.(this.partyPositions);
      return;
    }
    if (type === MessageType.EdgeView) {
      if (decodeEdgeView(frame, this.edgeView).kind !== "ok") {
        this.warnOnce("edgeView", "ignoring an edgeView frame that does not decode");
        return;
      }
      this.handlers.onEdgeView?.(this.edgeView);
      return;
    }
    if (type === MessageType.Explored) {
      const set = this.exploredSet(frame);
      if (set === null) {
        this.warnOnce("explored", "ignoring an explored frame that does not decode");
        return;
      }
      this.handlers.onExplored?.(set);
      return;
    }
    const id = `0x${type.toString(16).padStart(4, "0")}`;
    this.warnOnce(id, `ignoring server binary frames of type ${id}, which this page does not handle`);
  }

  /**
   * Decodes an `explored` frame into a set of the counts it names. The length is compared with the counts before a
   * set is made, so hostile counts size nothing.
   *
   * @param frame The frame.
   * @returns The set, or null when the frame is malformed.
   */
  private exploredSet(frame: Uint8Array): ExploredSet | null {
    if (frame.length < EXPLORED_HEADER_BYTES) {
      return null;
    }
    const columns = (frame[HEADER_BYTES] as number) | ((frame[HEADER_BYTES + 1] as number) << 8);
    const rows = (frame[HEADER_BYTES + 2] as number) | ((frame[HEADER_BYTES + 3] as number) << 8);
    if (columns < 1 || rows < 1 || frame.length !== exploredFrameBytes(columns, rows)) {
      return null;
    }
    const set = newExploredSet(columns, rows);
    return decodeExplored(frame, set).kind === "ok" ? set : null;
  }

  /**
   * Writes a warning the first time a key is met.
   *
   * @param key What the warning is about.
   * @param message The warning.
   */
  private warnOnce(key: string, message: string): void {
    if (this.warned.has(key)) {
      return;
    }
    this.warned.add(key);
    (this.handlers.warn ?? console.warn)(message);
  }
}
